import { MEASUREMENT_TYPES } from "../../config/enums/MeasurementTypes";
import { IWasteRecord } from "./wasteManagement.types";

export const normalizeWasteUnit = (
  unit: number | string | undefined,
  fallbackUnit: MEASUREMENT_TYPES = MEASUREMENT_TYPES.WEIGHT_KG
): MEASUREMENT_TYPES => {
  if (unit === MEASUREMENT_TYPES.WEIGHT_TON || unit === "ton" || unit === 2 || unit === "2") {
    return MEASUREMENT_TYPES.WEIGHT_TON;
  }
  if (unit === MEASUREMENT_TYPES.WEIGHT_KG || unit === "kg" || unit === 1 || unit === "1") {
    return MEASUREMENT_TYPES.WEIGHT_KG;
  }
  return fallbackUnit;
};

/** Convert a distribution value to kg (canonical unit for totals). */
export const toKg = (
  value: number,
  unit: MEASUREMENT_TYPES,
  fallbackUnit: MEASUREMENT_TYPES = MEASUREMENT_TYPES.WEIGHT_KG
): number => {
  const resolvedUnit = normalizeWasteUnit(unit, fallbackUnit);
  return resolvedUnit === MEASUREMENT_TYPES.WEIGHT_TON ? value * 1000 : value;
};

/** Convert kg to the target display/storage unit. */
export const fromKg = (valueKg: number, targetUnit: MEASUREMENT_TYPES): number =>
  targetUnit === MEASUREMENT_TYPES.WEIGHT_TON ? valueKg / 1000 : valueKg;

export const convertWeightValue = (
  value: number,
  fromUnit: MEASUREMENT_TYPES,
  toUnit: MEASUREMENT_TYPES
): number => fromKg(toKg(value, fromUnit), toUnit);

export const sumWasteDistributions = (
  records: Array<{ sideStreamsDistribution?: string | number; unit?: MEASUREMENT_TYPES | string | number }>,
  targetUnit: MEASUREMENT_TYPES,
  fallbackUnit: MEASUREMENT_TYPES = MEASUREMENT_TYPES.WEIGHT_KG
): number => {
  const totalKg = records.reduce((sum, record) => {
    const value = Number(record.sideStreamsDistribution || 0);
    if (!value) return sum;
    return sum + toKg(value, normalizeWasteUnit(record.unit, fallbackUnit));
  }, 0);

  return fromKg(totalKg, targetUnit);
};

export const mapWasteRecordFromBackend = (item: any): IWasteRecord => ({
  _id: item._id,
  managementName: item.managementName || "",
  wasteType: item.wasteType || "",
  managementTypeId: item.managementTypeId ? String(item.managementTypeId) : "1",
  sideStreamsDistribution: item.sideStreamsDistribution?.toString() || "",
  unit: normalizeWasteUnit(item.unit, MEASUREMENT_TYPES.WEIGHT_KG),
  moistureContent: item.moistureContent?.toString() || "",
  ingredientId: item.ingredientId ? String(item.ingredientId) : null
});

export const createEmptyWasteRecord = (
  defaultTypeId: string = "1",
  defaultUnit: MEASUREMENT_TYPES = MEASUREMENT_TYPES.WEIGHT_KG
): IWasteRecord => ({
  managementName: "",
  wasteType: "",
  managementTypeId: defaultTypeId,
  sideStreamsDistribution: "",
  unit: defaultUnit,
  moistureContent: "",
  ingredientId: null
});

export const buildWasteApiPayload = (recordData: IWasteRecord) => ({
  managementName: recordData.managementName,
  wasteType: recordData.wasteType || undefined,
  unit: normalizeWasteUnit(recordData.unit, MEASUREMENT_TYPES.WEIGHT_KG),
  managementTypeId: Number(recordData.managementTypeId),
  sideStreamsDistribution: parseFloat(recordData.sideStreamsDistribution),
  moistureContent: recordData.moistureContent ? parseFloat(recordData.moistureContent) : undefined,
  ingredientId: recordData.ingredientId ? String(recordData.ingredientId) : undefined
});
